import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import type { AddCardFields } from '../model/add-card-shared'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '#/shared/ui/accordion'
import { Button } from '#/shared/ui/button'
import { Checkbox } from '#/shared/ui/checkbox'
import { UserCardIcon } from '#/shared/ui/icons'
import { Input } from '#/shared/ui/input'
import { Label } from '#/shared/ui/label'
import { Skeleton } from '#/shared/ui/skeleton'

const NAME_MAX_LENGTH = 100
const STATE_MAX_LENGTH = 2
const COUNTRY_MAX_LENGTH = 2
const ZIP_MAX_LENGTH = 12

export function TextField({
  labelKey,
  placeholderKey,
  value,
  onChange,
  maxLength = NAME_MAX_LENGTH,
  inputMode,
}: {
  labelKey: string
  placeholderKey: string
  value: string
  onChange: (next: string) => void
  maxLength?: number
  inputMode?: 'numeric'
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-1 flex-col gap-0.5">
      <Label size="sm">{t(labelKey)}</Label>
      <Input
        inputSize="sm"
        inputMode={inputMode}
        maxLength={maxLength}
        placeholder={t(placeholderKey)}
        className="h-10 rounded-md px-2.5"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}

function CardholderSection({ fields }: { fields: AddCardFields }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-1">
      <Label size="sm">{t('addCard.cardholderName')}</Label>
      <div className="flex w-full items-start gap-3">
        <TextField
          labelKey="addCard.firstName"
          placeholderKey="addCard.firstNamePlaceholder"
          value={fields.form.firstName}
          onChange={(next) => fields.setField('firstName', next)}
        />
        <TextField
          labelKey="addCard.lastName"
          placeholderKey="addCard.lastNamePlaceholder"
          value={fields.form.lastName}
          onChange={(next) => fields.setField('lastName', next)}
        />
      </div>
      <span className="text-2xs text-gray-500">
        {t('addCard.cardholderNameHint')}
      </span>
    </div>
  )
}

function NicknameField({ fields }: { fields: AddCardFields }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-0.5">
      <Label size="sm">{t('addCard.nickname')}</Label>
      <Input
        inputSize="sm"
        maxLength={NAME_MAX_LENGTH}
        placeholder={t('addCard.nicknamePlaceholder')}
        className="h-10 rounded-md px-2.5"
        value={fields.form.nickname}
        onChange={(e) => fields.setField('nickname', e.target.value)}
      />
    </div>
  )
}

function DefaultCheckbox({ fields }: { fields: AddCardFields }) {
  const { t } = useTranslation()
  return (
    <label className="mt-1 flex cursor-pointer select-none items-center gap-2 text-sm text-gray-900">
      <Checkbox
        checked={fields.form.isDefault}
        onCheckedChange={(checked) =>
          fields.setField('isDefault', checked === true)
        }
      />
      {t('addCard.setAsDefault')}
    </label>
  )
}

function BillingAccordion({ fields }: { fields: AddCardFields }) {
  const { t } = useTranslation()
  return (
    <Accordion type="single" collapsible>
      <AccordionItem
        value="billing"
        className="-mx-1.5 border-t border-gray-200"
      >
        <AccordionTrigger className="px-1.5">
          <span className="flex flex-1 items-center gap-2">
            {t('addCard.billing.title')}
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-2xs-plus font-normal text-gray-500">
              {t('addCard.billing.optional')}
            </span>
          </span>
        </AccordionTrigger>
        <AccordionContent className="flex flex-col gap-3 px-1.5 pb-1.5">
          <TextField
            labelKey="addCard.billing.address"
            placeholderKey="addCard.billing.addressPlaceholder"
            value={fields.billing.address}
            onChange={(next) => fields.setBillingField('address', next)}
          />
          <TextField
            labelKey="addCard.billing.apartment"
            placeholderKey="addCard.billing.apartmentPlaceholder"
            value={fields.billing.apartment}
            onChange={(next) => fields.setBillingField('apartment', next)}
          />
          <div className="flex w-full items-start gap-3">
            <TextField
              labelKey="addCard.billing.city"
              placeholderKey="addCard.billing.cityPlaceholder"
              value={fields.billing.city}
              onChange={(next) => fields.setBillingField('city', next)}
            />
            <TextField
              labelKey="addCard.billing.state"
              placeholderKey="addCard.billing.statePlaceholder"
              value={fields.billing.state}
              maxLength={STATE_MAX_LENGTH}
              onChange={(next) => fields.setBillingField('state', next)}
            />
          </div>
          <div className="flex w-full items-start gap-3">
            <TextField
              labelKey="addCard.billing.zip"
              placeholderKey="addCard.billing.zipPlaceholder"
              value={fields.billing.zip}
              maxLength={ZIP_MAX_LENGTH}
              onChange={(next) => fields.setBillingField('zip', next)}
            />
            <TextField
              labelKey="addCard.billing.country"
              placeholderKey="addCard.billing.countryPlaceholder"
              value={fields.billing.country}
              maxLength={COUNTRY_MAX_LENGTH}
              onChange={(next) => fields.setBillingField('country', next)}
            />
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}

export function AddCardBody({
  fields,
  cardField,
}: {
  fields: AddCardFields
  cardField: ReactNode
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-5 pt-4 pb-4">
      {cardField}
      <CardholderSection fields={fields} />
      <NicknameField fields={fields} />
      <DefaultCheckbox fields={fields} />
      <BillingAccordion fields={fields} />
    </div>
  )
}

export function AddCardFooter({
  onCancel,
  onPrimary,
  canSubmit,
  isSubmitting,
}: {
  onCancel: () => void
  onPrimary: () => void
  canSubmit: boolean
  isSubmitting: boolean
}) {
  const { t } = useTranslation()
  return (
    <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
      <Button
        type="button"
        variant="outline"
        size="xs"
        pressEffect
        className="px-3.75"
        onClick={onCancel}
      >
        {t('addCard.cancel')}
      </Button>
      <Button
        type="button"
        size="xs"
        pressEffect
        className="px-3.75"
        disabled={!canSubmit}
        onClick={onPrimary}
      >
        {isSubmitting ? t('addCard.submitting') : t('addCard.submit')}
      </Button>
    </footer>
  )
}

export function BootstrapLoading() {
  return (
    <div className="flex flex-col gap-3 px-5 pt-4 pb-4">
      <Skeleton className="h-10 w-full rounded-md" />
      <Skeleton className="h-10 w-full rounded-md" />
      <Skeleton className="h-10 w-full rounded-md" />
    </div>
  )
}

function NoticeCard({
  titleKey,
  hintKey,
}: {
  titleKey: string
  hintKey: string
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2.5 px-6 py-12 text-center">
      <div className="flex size-11 items-center justify-center rounded-full bg-white ring-1 ring-gray-100 ring-inset">
        <UserCardIcon className="size-5 text-gray-300" />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-gray-900">{t(titleKey)}</p>
        <p className="text-xs leading-relaxed text-gray-400">{t(hintKey)}</p>
      </div>
    </div>
  )
}

export function GatewayUnavailable() {
  return (
    <NoticeCard
      titleKey="addCard.unavailable.title"
      hintKey="addCard.unavailable.hint"
    />
  )
}

export function HttpsRequiredNotice() {
  return (
    <NoticeCard
      titleKey="addCard.httpsRequired.title"
      hintKey="addCard.httpsRequired.hint"
    />
  )
}
